import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Radio, X, Mic, MicOff, Video, VideoOff, Eye } from "lucide-react";
import { toast } from "sonner";

export default function GoLive() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [permErr, setPermErr] = useState("");
  const [title, setTitle] = useState("");
  const [featuredId, setFeaturedId] = useState("");
  const [products, setProducts] = useState([]);
  const [stream, setStream] = useState(null); // backend stream record
  const [audio, setAudio] = useState(true);
  const [video, setVideo] = useState(true);

  const cleanupCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  // Request camera + load own products
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: true,
        });
        if (!active) {
          camStream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = camStream;
        if (videoRef.current) videoRef.current.srcObject = camStream;
      } catch (e) {
        setPermErr("Camera/mic permission denied. Tap the lock icon → allow camera & mic.");
      }
    })();

    if (user?.user_id) {
      api.get("/products", { params: { vendor_id: user.user_id, limit: 30 } })
         .then(({ data }) => setProducts(data))
         .catch(() => setProducts([]));
    }

    return () => { active = false; cleanupCamera(); };
  // eslint-disable-next-line
  }, []);

  const toggleAudio = () => {
    if (!streamRef.current) return;
    const next = !audio;
    streamRef.current.getAudioTracks().forEach((t) => (t.enabled = next));
    setAudio(next);
  };

  const toggleVideo = () => {
    if (!streamRef.current) return;
    const next = !video;
    streamRef.current.getVideoTracks().forEach((t) => (t.enabled = next));
    setVideo(next);
  };

  const startBroadcast = async () => {
    if (!title.trim()) {
      toast.error("Give your stream a title");
      return;
    }
    try {
      const { data } = await api.post("/streams/start", {
        title: title.trim(),
        featured_product_id: featuredId || null,
      });
      setStream(data);
      toast.success("You're LIVE 🔴");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't go live");
    }
  };

  const endBroadcast = async () => {
    if (!stream) {
      cleanupCamera();
      navigate("/sell");
      return;
    }
    try {
      await api.post(`/streams/${stream.stream_id}/end`);
      cleanupCamera();
      toast.success("Stream ended");
      navigate("/sell");
    } catch {
      cleanupCamera();
      navigate("/sell");
    }
  };

  // Poll viewer count while live
  const [viewers, setViewers] = useState(0);
  useEffect(() => {
    if (!stream?.stream_id || stream.status !== "live") return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/streams/${stream.stream_id}`);
        setViewers(data.viewers || 0);
      } catch { /* ignore */ }
    };
    tick();
    const i = setInterval(tick, 5000);
    return () => clearInterval(i);
  }, [stream?.stream_id, stream?.status]);

  if (!user?.is_vendor) {
    return (
      <div className="min-h-[80dvh] grid place-items-center text-center px-6" data-testid="golive-not-vendor">
        <div>
          <h2 className="text-2xl font-black tracking-tighter">Vendors only.</h2>
          <p className="text-zinc-400 mt-2">Open your shop to go live.</p>
          <button
            onClick={() => navigate("/sell")}
            className="mt-5 h-11 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
            data-testid="golive-go-sell-btn"
          >
            Open my shop
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[100dvh] bg-black overflow-hidden" data-testid="golive-page">
      {/* Camera preview */}
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="golive-camera-preview"
      />

      {/* Permission error */}
      {permErr && (
        <div className="absolute inset-0 grid place-items-center bg-black/80 px-6 text-center">
          <div>
            <p className="text-pink-300 font-semibold">{permErr}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 h-10 px-5 rounded-full bg-white/10 border border-white/15"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Top status bar */}
      <div className="absolute top-0 left-0 right-0 z-10 px-4 pt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {stream && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 text-white text-xs font-black tracking-widest pulse-glow" data-testid="golive-live-badge">
              <span className="w-2 h-2 rounded-full bg-white" /> LIVE
            </span>
          )}
          {stream && (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full glass text-xs font-bold text-white" data-testid="golive-viewers">
              <Eye className="w-3 h-3" /> {viewers}
            </span>
          )}
        </div>
        <button
          onClick={endBroadcast}
          className="w-9 h-9 grid place-items-center rounded-full glass text-white hover:bg-white/10"
          aria-label="Close"
          data-testid="golive-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Setup form (before going live) */}
      {!stream && (
        <div className="absolute bottom-6 left-0 right-0 px-4">
          <div className="max-w-md mx-auto rounded-3xl p-5 glass-strong space-y-3">
            <div className="space-y-1.5">
              <Label className="text-zinc-300 text-xs uppercase tracking-widest">Stream title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Cooking birria — pull up!"
                className="h-12 bg-white/5 border-white/10 rounded-2xl text-white"
                data-testid="golive-title-input"
              />
            </div>
            {products.length > 0 && (
              <div className="space-y-1.5">
                <Label className="text-zinc-300 text-xs uppercase tracking-widest">Feature a product (optional)</Label>
                <Select value={featuredId} onValueChange={setFeaturedId}>
                  <SelectTrigger className="h-11 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="golive-product-select">
                    <SelectValue placeholder="Pick one" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white max-h-60">
                    {products.map((p) => (
                      <SelectItem key={p.product_id} value={p.product_id}>
                        {p.name} — ${p.price.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-2 pt-1">
              <button onClick={toggleVideo} className="w-11 h-11 grid place-items-center rounded-full bg-white/10 border border-white/10" aria-label="Toggle video" data-testid="golive-toggle-video">
                {video ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4 text-red-400" />}
              </button>
              <button onClick={toggleAudio} className="w-11 h-11 grid place-items-center rounded-full bg-white/10 border border-white/10" aria-label="Toggle audio" data-testid="golive-toggle-audio">
                {audio ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-red-400" />}
              </button>
              <button
                onClick={startBroadcast}
                className="ml-auto h-12 px-6 rounded-full bg-red-500 hover:bg-red-400 text-white font-black tracking-tight glow-pink flex items-center gap-2"
                data-testid="golive-start-btn"
              >
                <Radio className="w-4 h-4" /> Go Live
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live actions bar */}
      {stream && (
        <div className="absolute bottom-6 left-0 right-0 px-4">
          <div className="max-w-md mx-auto rounded-full px-4 py-3 glass-strong flex items-center gap-2">
            <p className="font-bold text-sm flex-1 truncate">{stream.title}</p>
            <button onClick={toggleVideo} className="w-10 h-10 grid place-items-center rounded-full bg-white/10" aria-label="Toggle video">
              {video ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4 text-red-400" />}
            </button>
            <button onClick={toggleAudio} className="w-10 h-10 grid place-items-center rounded-full bg-white/10" aria-label="Toggle audio">
              {audio ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-red-400" />}
            </button>
            <button
              onClick={endBroadcast}
              className="h-11 px-5 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold"
              data-testid="golive-end-btn"
            >
              End
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
